import type { Ref, WritableComputedRef } from 'vue'
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'
import type { TutorTaskDetails, TutorTaskEditForm } from './tutor-tasks/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { TaskStatus, TaskType } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { AppRoute } from '@shared/config'

import { TutorTaskModal } from './tutor-tasks/enums'
import { useDisciplineStore } from './store'
import { useDisciplineTasksStore } from './tasks/store'
import { useTutorTaskShowApi } from '../api/tutor-tasks/show.hook'
import { useTutorTaskUpdateApi } from '../api/tutor-tasks/update.hook'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplineTasksRefresh } from './discipline-tasks-refresh.hook'
import { getDisciplineName } from './discipline-general-info'
import {
  TASK_EMPTY_CELL,
  buildTaskEditForm,
  buildTaskUpdatePayload,
  formatTaskDate,
  getFilePackOptionText,
  isFilePackTaskType,
  isReasonTaskType,
} from './tutor-tasks/tutor-tasks'

interface TaskInfoLink {
  text: string
  url: string | null
}

interface ModalDisciplineTaskEditReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTask: Ref<TutorTaskDetails | null>
  refForm: Ref<TutorTaskEditForm | null>
  refTutorId: WritableComputedRef<number | null>
  refDueDate: WritableComputedRef<Date | null>
  refTitle: ComputedRef<string>
  refTypeLabel: ComputedRef<string>
  refIsManual: ComputedRef<boolean>
  refIsProgressReport: ComputedRef<boolean>
  refIsTaskOpen: ComputedRef<boolean>
  refHasFilePackSelect: ComputedRef<boolean>
  refHasReason: ComputedRef<boolean>
  refCreatedAt: ComputedRef<string>
  refLessonDate: ComputedRef<string>
  refCompletionDate: ComputedRef<string>
  refDateRange: ComputedRef<string>
  refKid: ComputedRef<TaskInfoLink>
  refDiscipline: ComputedRef<TaskInfoLink>
  refStatusOptions: ComputedRef<NativeSelectOption[]>
  refFilePackOptions: ComputedRef<NativeSelectOption[]>
  refFilePackUrl: ComputedRef<string | null>
  refIsChanged: ComputedRef<boolean>
  canManageTutorTasks: ComputedRef<boolean>
  emptyCell: string
  setStatus: (option: NativeSelectOption) => void
  setFilePack: (option: NativeSelectOption) => void
  setDescription: (event: AppTextareaEvent) => void
  openFilePack: () => void
  closeModal: () => void
  saveTask: () => Promise<void>
}

// codestyle-ignore: date-format-enum — short month-day label; DateFormat has no month-day member (decided 2026-10-29)
const SHORT_DATE_FORMAT = 'MMM DD'

export const useModalDisciplineTaskEdit = (): ModalDisciplineTaskEditReturn => {
  const route = useRoute()
  const dayjs = useDayjs()
  const disciplineStore = useDisciplineStore()
  const store = useDisciplineTasksStore()
  const listsStore = useListsStore()
  const { canManageTutorTasks } = useDisciplinePrivileges()
  const { writeAndRefresh } = useDisciplineTasksRefresh()

  const refIsLoading = ref(false)
  const refTask = ref<TutorTaskDetails | null>(null)
  const refForm = ref<TutorTaskEditForm | null>(null)

  const refIsOpen = computed<boolean>(() => store.getActiveModal === TutorTaskModal.Edit)

  const refTutorId = computed<number | null>({
    get: () => refForm.value?.tutor_id ?? null,
    set: (value) => {
      if (refForm.value && value) refForm.value.tutor_id = value
    },
  })

  const refDueDate = computed<Date | null>({
    get: () => (refForm.value?.due_date ? dayjs(refForm.value.due_date).toDate() : null),
    set: (value) => {
      if (refForm.value) refForm.value.due_date = value ? dayjs(value).format(DateFormat.Default) : null
    },
  })

  const refTitle = computed<string>(() => `Task  # ${refTask.value?.id ?? ''}`)
  const refTypeLabel = computed<string>(() =>
    (refTask.value && listsStore.getLists?.tutor_task.type[refTask.value.type]) || '')
  const refIsManual = computed<boolean>(() => refTask.value?.type === TaskType.Manual)
  const refIsProgressReport = computed<boolean>(() => refTask.value?.type === TaskType.ProgressReport)
  const refIsTaskOpen = computed<boolean>(() => refTask.value?.status === TaskStatus.Planned)
  // The progress-report layout of the legacy modal had no file-pack select.
  const refHasFilePackSelect = computed<boolean>(() =>
    !!refTask.value && isFilePackTaskType(refTask.value.type) && !refIsProgressReport.value
    && refTask.value.file_packs.length > 0)
  const refHasReason = computed<boolean>(() => !!refTask.value && isReasonTaskType(refTask.value.type))

  const refCreatedAt = computed<string>(() => formatTaskDate(refTask.value?.created_at ?? null))
  const refLessonDate = computed<string>(() => formatTaskDate(refTask.value?.booking?.start ?? null) || TASK_EMPTY_CELL)
  const refCompletionDate = computed<string>(() =>
    formatTaskDate(refTask.value?.completed_date ?? null) || TASK_EMPTY_CELL)

  const refDateRange = computed<string>(() => {
    const task = refTask.value
    if (!task) return ''

    const created = dayjs(task.created_at).format(SHORT_DATE_FORMAT)
    return task.due_date ? `${created} - ${dayjs(task.due_date).format(SHORT_DATE_FORMAT)}` : created
  })

  const refKid = computed<TaskInfoLink>(() => {
    const kidId = refTask.value?.discipline?.kid_id
    if (!kidId) return { text: TASK_EMPTY_CELL, url: null }

    const disciplineKid = disciplineStore.getDiscipline?.kid
    const kidName = disciplineKid?.id === kidId ? disciplineKid.name : ''

    return {
      text: `#${kidId} ${kidName}`.trim(),
      url: resolveUrl(AppRoute.KidsShow, { id: kidId }),
    }
  })

  const refDiscipline = computed<TaskInfoLink>(() => {
    const discipline = refTask.value?.discipline
    if (!discipline) return { text: TASK_EMPTY_CELL, url: null }

    return {
      text: getDisciplineName(discipline.code, listsStore.getLists?.discipline.code),
      url: resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: discipline.id }),
    }
  })

  const refStatusOptions = computed<NativeSelectOption[]>(() =>
    Object.entries(listsStore.getLists?.tutor_task.status ?? {}).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refForm.value?.status,
    })))

  const refFilePackOptions = computed<NativeSelectOption[]>(() => {
    const categories = listsStore.getLists?.file.category
    return (refTask.value?.file_packs ?? []).map(pack => ({
      id: pack.id,
      value: pack.id,
      text: getFilePackOptionText(pack, categories),
      selected: pack.id === refForm.value?.file_pack_id,
    }))
  })

  /** The Files tab opens the pack from the `#<packId>` hash, as the legacy button relied on. */
  const refFilePackUrl = computed<string | null>(() => {
    const packId = refForm.value?.file_pack_id
    const path = packId ? resolveUrl(AppRoute.DisciplinesFiles, { id: route.params.id as string }) : null
    return path ? `${path}#${packId}` : null
  })

  const refIsChanged = computed<boolean>(() =>
    !!refTask.value && !!refForm.value && Object.keys(buildTaskUpdatePayload(refTask.value, refForm.value)).length > 0)

  const setStatus = (option: NativeSelectOption): void => {
    if (refForm.value) refForm.value.status = option.value as TaskStatus
  }

  const setFilePack = (option: NativeSelectOption): void => {
    if (refForm.value) refForm.value.file_pack_id = option.value === null ? null : Number(option.value)
  }

  const setDescription = (event: AppTextareaEvent): void => {
    if (refForm.value) refForm.value.description = event.value
  }

  const openFilePack = (): void => {
    if (refFilePackUrl.value) window.open(refFilePackUrl.value, '_blank')
  }

  const closeModal = (): void => {
    if (refIsLoading.value) return
    store.closeModal()
  }

  const loadTask = async (taskId: number): Promise<void> => {
    refTask.value = null
    refForm.value = null

    const task = await useTutorTaskShowApi({
      taskId,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!task) {
      store.closeModal()
      return
    }

    refTask.value = task
    refForm.value = buildTaskEditForm(task)
  }

  const saveTask = async (): Promise<void> => {
    const task = refTask.value
    const form = refForm.value
    if (!task || !form || refIsLoading.value || !refIsChanged.value) return

    await writeAndRefresh(refIsLoading, () => useTutorTaskUpdateApi({
      taskId: task.id,
      payload: buildTaskUpdatePayload(task, form),
      successMessage: 'The task was successfully edit.',
    }))
  }

  watch(refIsOpen, (isOpen) => {
    const taskId = store.getActiveTask?.id
    if (isOpen && taskId) void loadTask(taskId)
  })

  return {
    refIsOpen,
    refIsLoading,
    refTask,
    refForm,
    refTutorId,
    refDueDate,
    refTitle,
    refTypeLabel,
    refIsManual,
    refIsProgressReport,
    refIsTaskOpen,
    refHasFilePackSelect,
    refHasReason,
    refCreatedAt,
    refLessonDate,
    refCompletionDate,
    refDateRange,
    refKid,
    refDiscipline,
    refStatusOptions,
    refFilePackOptions,
    refFilePackUrl,
    refIsChanged,
    canManageTutorTasks,
    emptyCell: TASK_EMPTY_CELL,
    setStatus,
    setFilePack,
    setDescription,
    openFilePack,
    closeModal,
    saveTask,
  }
}

